import { LitElement, html } from 'lit'
import { property } from 'lit/decorators.js'
import { isRTL, returnRTEHTML } from '@gbds/core/src/ts/utils/helper-utils'
import { isShiftTab, isTabOnly } from '@gbds/core/src/ts/utils'
import { getFirstAndLastFocusableElement } from '@gbds/core/src/ts/utils/firstlast-focusable-elements'
import { setAttribute, getAttribute } from '@gbds/core/src/ts/utils/attribute-utils'
import { Overlay } from '../overlay'
import {
	AuthData,
	UnauthHeaderNode,
	UnauthFooterNode,
	UnauthKeys,
	AuthLinkKeys,
	AuthFlyoutKeys,
	AuthOpenEvent,
	AuthKeys,
} from '../../models/my-account'
import { ProfileKeys, RewardsKeys, NotificationKeys, VehicleKeys, RewardsState } from '../../models/user-info'
import { AuthLiterals } from '../../config/auth-enumerations'
import { lockBodyScroll, unlockBodyScroll } from '../../utils/body-scroll-lock'
import { parseTokenForEmail, parseTokenForName } from '../../utils/auth/verify-token'
import { getProfileDisplayName } from '../../utils/profile-name-utils'
import { initUserData } from '../../config/user-info-config'
import { NavConfig, URLConfig } from './configs/base'

/**
 * @noInheritDoc
 */
export class GbpeMyaccountFlyout extends LitElement {
	@property({ type: String, reflect: true })
	flyoutState: string

	@property({ type: Boolean, reflect: true })
	flyoutOpen: boolean

	@property({ type: Object, reflect: true })
	authFlyoutData: AuthFlyoutKeys

	@property({ type: Object, reflect: true })
	authNavData: AuthKeys

	@property({ type: Array, reflect: true })
	authLinkData: AuthLinkKeys[]

	@property({ type: Object, reflect: true })
	authTierData: JSON

	@property({ type: Object, reflect: true })
	fallbackData: JSON

	@property({ type: Object })
	private _profile: ProfileKeys

	@property({ type: Boolean, reflect: true })
	private _profileFallback: boolean

	@property({ type: Boolean, reflect: true })
	private _profileLoading: boolean

	@property({ type: Object, reflect: true })
	private _rewards: RewardsKeys

	@property({ type: Array })
	private _vehicles: VehicleKeys[]

	@property({ type: Boolean, reflect: true })
	private _vehiclesFallback: boolean

	@property({ type: Object, reflect: true })
	private _notifications: NotificationKeys

	private _overlay: Overlay
	private _unauthNavData: UnauthKeys
	private _unauthHeaderNode: UnauthHeaderNode
	private _unauthFooterNode: UnauthFooterNode
	private _rewardsState: RewardsState
	private _closeButton: HTMLButtonElement
	private _lastFocusableElement: HTMLElement
	private _parentButton: HTMLButtonElement
	private _urlConfig: URLConfig
	protected _backupVehicleImagePath: string

	constructor(backupVehicleImagePath: string, navConfig: NavConfig) {
		super()
		document.addEventListener('gb-gn-closeMyAccountFlyout', e => this.closeFlyout(e as CustomEvent))
		document.addEventListener('gbpe-myAccountFlyout-open', e => this.openFlyout(e as AuthOpenEvent))
		document.addEventListener('gb-overlayClosedFromClick', e => this.closeFlyout(e))
		document.addEventListener('keyup', e => this._escapeListenerHandler(e))
		document.addEventListener('gb-updateRewards', e => this.populateRewards(e))
		document.addEventListener('gb-updateProfile', e => this.populateProfile(e as CustomEvent))
		this._backupVehicleImagePath = backupVehicleImagePath
		this._urlConfig = navConfig.urls

		if (isRTL()) {
			this.style.setProperty('--mobile-flyout-translate', 'translate(50%)')
			this.style.setProperty('--flyout-translateX', 'translate(-100%)')
		}
	}

	private openFlyout(e: CustomEvent) {
		lockBodyScroll()
		this.setFlyoutAuthState(e.detail.data.authenticationState)
		this.setFlyoutOpen('true')
		this.populateAuthorData(e.detail.data).then(() => {
			this.setFlyoutHeightStyles(e.detail.data)
		})
	}

	private setFlyoutHeightStyles(eventDetail: AuthData) {
		const vehiclesDisabled = eventDetail.authNavData.disableAuthVehicles
		const authState = eventDetail.authenticationState

		if (vehiclesDisabled === 'true' && authState === 'AUTH') {
			this.setFlyoutHeight()
			this.setFlyoutHeightAuto()
		}
	}

	private setFlyoutHeight() {
		const myaccFlyoutBody = this.querySelector('myacc-auth-flyout-body') as HTMLElement
		const flyoutHeight = this.querySelector('myacc-header')?.clientHeight
		myaccFlyoutBody.style.height = `calc(100% - ${flyoutHeight}px)`
	}

	private setFlyoutHeightAuto(isFlyoutClosed = 'false') {
		//current styling with no vehicle data requires a small flyout with updated css and scrolling when
		//several links are authored instead.
		const myaccFlyoutBody = this.querySelector('myacc-auth-flyout-body') as HTMLElement
		const MyaccFlyoutHeight = this.clientHeight
		const viewPort = window.innerHeight

		if (isFlyoutClosed === 'true') {
			this.style.height = ''
			myaccFlyoutBody.style.height = ''
		} else {
			this.style.height = viewPort > MyaccFlyoutHeight ? 'auto' : '100%'
		}
	}

	private setFlyoutAuthState(state: string) {
		setAttribute(this, 'flyoutState', state)
	}

	private setFlyoutOpen(open: string) {
		setAttribute(this, 'flyoutOpen', open)
	}

	get isFlyoutOpen(): boolean {
		return this.getAttribute('flyoutOpen') === 'true' ? true : false
	}

	private async populateAuthorData(eventDetail: AuthData) {
		this._overlay = eventDetail.myaaccountOverlay
		this._unauthHeaderNode = eventDetail.unauthFlyoutHeaderData
		this._unauthNavData = eventDetail.unauthNavData
		this.authNavData = eventDetail.authNavData
		if (this.flyoutState === AuthLiterals.UNAUTH) {
			this._unauthFooterNode = eventDetail.unauthFlyoutFooterData
		} else {
			this._profile = eventDetail.userData.profile
			this._profileFallback = eventDetail.userData.profileFallback ?? false
			this._profileLoading = eventDetail.userData.profileLoading ?? false
			this._rewards = eventDetail.userData.rewards
			this._rewardsState = eventDetail.rewardsState
			this._notifications = eventDetail.userData.notification
			if (this.authNavData.disableAuthVehicles !== 'true') {
				try {
					const vehicleData = await window.gbpe.user.vehicles
					this._vehicles = vehicleData.data['garage']
					this._vehiclesFallback = false
				} catch (e) {
					console.log('Vehicle API Call Failed!!')
					this._vehiclesFallback = true
				}
			} else {
				return Promise.resolve(true)
			}
		}
	}

	private populateRewards(eventDetail) {
		this._rewards = eventDetail.detail.rewards
		this._rewardsState = eventDetail.detail.rewardsState
	}

	private populateProfile(eventDetail: CustomEvent) {
		this._profile = eventDetail.detail.profile
		this._profileFallback = eventDetail.detail.profileFallback
		this._profileLoading = eventDetail.detail.profileLoading
	}

	private closeFlyout(event: Event) {
		if (this.isFlyoutOpen) {
			unlockBodyScroll()
			this.setFlyoutOpen('false')
			document.dispatchEvent(new CustomEvent('flyout-close'))
			if (this.authNavData.disableAuthVehicles === 'true' && this.flyoutState === 'AUTH') {
				this.setFlyoutHeightAuto('true')
			}
			if (event.type != 'gb-overlayClosedFromClick') {
				this._overlay.toggleOverlay()

				if (this._parentButton) {
					this._parentButton.focus()

					event.preventDefault()
				}
			}
		}
	}

	private clearStorage() {
		const userData = initUserData(this._urlConfig)
		sessionStorage.removeItem(userData.profile.storageKey)
		sessionStorage.removeItem(userData.rewards.storageKey)
		sessionStorage.removeItem(userData.vehicles.storageKey)
		sessionStorage.removeItem(userData.missingInfo.storageKey)
	}

	private _escapeListenerHandler(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			const openFlyout = getAttribute(this, 'flyoutOpen')
			if (openFlyout === 'true') {
				this.closeFlyout(event)
			}
			event.preventDefault()
		}
	}

	private signin() {
		if (this._unauthHeaderNode.signInLink) {
			window.location.assign(this._unauthHeaderNode.signInLink)
		} else {
			if (this.flyoutState === AuthLiterals.UNAUTH) {
				this.callAuthentication()
			}
		}
	}

	private async callAuthentication() {
		if (window.gbpe) {
			await this.renderAuthState()
			window.gbpe.auth.loginRedirect()
		}
	}

	/**
	 * calls callRegister function with register link variable if flyout state is not authenticated
	 * @private
	 */
	private register() {
		if (this.flyoutState === AuthLiterals.UNAUTH) {
			this.callRegister(this._unauthHeaderNode.registerLink)
		}
	}

	/**
	 * calls registers function of headless auth if window gbpe is initialized
	 * @param url - url denoting the create Account URL
	 * @private
	 */
	private async callRegister(url: string) {
		if (window.gbpe) {
			await this.renderAuthState()
			//Example format: 'https://www-chevrolet-com.prd2.wpx.gm.com/myaccount/authorize/create-account/email-input'
			window.gbpe.auth.registerRedirect(url)
		}
	}

	private async renderAuthState() {
		await window.gbpe.auth.loaded
	}

	private _addButtonListeners() {
		this._parentButton.addEventListener('keydown', e => {
			this._setButtonElements()
			this._handleParentButtonTabEvent(e)
			this._handleParentButtonShiftTabEvent(e)
		})
		if (this._closeButton) {
			this._closeButton.addEventListener('keydown', e => {
				this._addTabListeners()
				this._handleParentButtonShiftTabEvent(e)
			})
		}
	}

	private _setButtonElements() {
		this._closeButton = this.querySelector('.stat-button-close')
		this._parentButton = document
			.querySelector('gb-global-nav')
			.shadowRoot.querySelector('gb-myaccount-nav')
			.querySelector('button')
		this._setLastTabbableElements()
		this._addButtonListeners()
	}

	private _setLastTabbableElements() {
		if (!this._lastFocusableElement) {
			const { last } = getFirstAndLastFocusableElement(this.shadowRoot || this)
			this._lastFocusableElement = last
		}
	}

	private _addTabListeners() {
		if (!this._isLastFocusableElement(this._closeButton)) {
			this._lastFocusableElement.addEventListener('keydown', e => this._handleLastFocusableElementTabEvent(e))
		}
	}

	private _handleParentButtonTabEvent(e: KeyboardEvent) {
		/**
		 * Focus the first focusable event when tabbing from the parent button when the flyout is open.
		 */

		if (isTabOnly(e) && this.isFlyoutOpen) {
			this._closeButton.focus()

			e.preventDefault()
		}
	}

	private _handleParentButtonShiftTabEvent(e: KeyboardEvent) {
		if (isShiftTab(e) && this.isFlyoutOpen) {
			/**
			 * Close the flyout when the user shift + tabs from the parent button and the flyout is open.
			 */
			this.closeFlyout(e)

			e.preventDefault()
		}
	}

	/**
	 * Focus the close button when tabbing from the last focusable element inside the flyout
	 */
	private _handleLastFocusableElementTabEvent(e: KeyboardEvent) {
		if (isTabOnly(e) && this.isFlyoutOpen) {
			this._closeButton.focus()

			e.preventDefault()
		}
	}

	private _isLastFocusableElement(el: HTMLElement) {
		return el.isSameNode(this._lastFocusableElement)
	}

	private closeContent() {
		return html`<myacc-flyout-close>
			<button class="stat-button-close" name="close" @click="${this.closeFlyout}" data-dtm="my account"></button>
		</myacc-flyout-close>`
	}

	private addLoader() {
		// Create a style element
		const style = document.createElement('style')
		style.innerHTML = `
			@keyframes rotate-360 {
				from {
					transform: rotate(0deg);
				}
				to {
					transform: rotate(359deg);
				}
			}
			.gb-loader {
				animation: rotate-360 .75s infinite linear;
				border: .625rem solid rgba(255,255,255,0.2);
				border-radius: 100%;
				height: 5.625rem;
				margin: 30% auto;
				transform: translateZ(0);
				width: 5.625rem;
			}
			.gb-loader::before {
				border-bottom: .625rem solid transparent;
				border-left: .625rem solid transparent;
				border-radius: 100% !important;
				border-right: .625rem solid transparent;
				border-top: .625rem solid #073980;
				box-sizing: content-box;
				content: '';
				display: block;
				height: 100% !important;
				left: -0.625rem;
				position: absolute;
				top: -0.625rem;
				width: 100% !important;
			}
		`

		// Append the style element to the head
		document.head.appendChild(style)

		// Create the loader div
		const loaderDiv = document.createElement('div')
		loaderDiv.className = 'gb-loader'

		// Append the loader div to the body
		document.body.appendChild(loaderDiv)
	}

	private signout() {
		if (window.location.hostname.includes('experience')) {
			document.cookie = 'gm-energy-destroy=true; domain=.gm.com; path=/; max-age=' + 60 * 60 * 24 * 30 // Cookie expires in 30 days
		}
		//Force Refresh on 'Back'
		window.addEventListener('pageshow', function (event) {
			const historyTraversal =
				event.persisted ||
				(typeof window.performance != 'undefined' && window.performance.navigation.type === 2)
			if (historyTraversal) {
				// Handle page restore.
				window.location.reload()
			}
		})

		//Closing Flyout
		document.body.innerHTML = ''
		sessionStorage.isLogout = true
		this.addLoader()
		this.clearStorage()
		window.gbpe.auth.logout()
		document.dispatchEvent(new CustomEvent('signedout'))
	}

	private unauthheaderContent() {
		return html`<myacc-header nav-state=${this.flyoutState} 
        enable-unauth-rewards=${this._unauthNavData.enableUnauthRewards}>
            ${this.closeContent()}
			${this._unauthHeaderNode.headingFlyout && this._unauthHeaderNode.headingFlyout !== '' ? this.showHeadingFlyout() : ''}
            <myacc-signin>
                <myacc-signin-img>
                    <img src=${this._unauthHeaderNode.avatarImg} 
                        alt="${this._unauthHeaderNode.avatarImgAltText}"></img>
                </myacc-signin-img>
                <myacc-signin-message>
                    ${returnRTEHTML(this._unauthHeaderNode.signInMsg)}
                </myacc-signin-message>
            </myacc-signin>
            <myacc-signin-button>
                <button class="stat-button-link" name="signin" @click="${this.signin}" data-dtm="my account">${
			this._unauthHeaderNode.signinButtonLabel
		}</button>
            </myacc-signin-button>
            ${
				this.flyoutState === AuthLiterals.UNAUTH && this._unauthNavData.enableUnauthRewards === 'true'
					? this.registerContent()
					: ''
			}
        </myacc-header>`
	}

	private showHeadingFlyout() {
		return html`<heading-flyout>${returnRTEHTML(this._unauthHeaderNode.headingFlyout)}</heading-flyout>`
	}

	private registerContent() {
		return html`<myacc-register>
			<myacc-register-text>${returnRTEHTML(this._unauthHeaderNode.registerMsg)}</myacc-register-text>
			<myacc-register-button>
				<button @click="${this.register}" class="stat-button-link" data-dtm="my account">
					${this._unauthHeaderNode.registerButtonLabel}
				</button>
			</myacc-register-button>
		</myacc-register>`
	}

	private unauthfooterContent() {
		return html`<myacc-footer>
            <myacc-placeholder>
                <myacc-placeholder-message>
                    ${returnRTEHTML(this._unauthFooterNode?.footerMsg)}
                </myacc-placeholder-message>
                <myacc-placeholder-img>
                    <img src=${this._unauthFooterNode?.footerImg} alt="${
			this._unauthFooterNode?.footerImgAltText
		}"></img>
                </myacc-placeholder-img>
            </myacc-placeholder>
        </myacc-footer>`
	}

	private _getCachedName(): string | null {
		const idTokenName =
			window.gbpe.auth.idToken && window.gbpe.auth.idToken !== ''
				? parseTokenForName(window.gbpe.auth.idToken)
				: null
		const idTokenEmail =
			window.gbpe.auth.idToken && window.gbpe.auth.idToken !== ''
				? parseTokenForEmail(window.gbpe.auth.idToken)
				: null

		return idTokenName || idTokenEmail || 'My Account'
	}

	private authheaderContent() {
		return html`<myacc-header auth enable-auth-rewards=${this._unauthNavData.enableAuthRewards}>
			${this.closeContent()}
			${this._hasValidProfile()
				? html`<myacc-profile-info>
						<myacc-nav-initial data-initial=${this._profile.initial}>
							<myacc-nav-name>${getProfileDisplayName(this._profile.first_name)}</myacc-nav-name>
							<myacc-nav-email class="myacc-nav-email"
								>${this.authNavData.disableRewardsInNav === 'true'
									? ''
									: this._profile?.login_id}</myacc-nav-email
							>
						</myacc-nav-initial>
				  </myacc-profile-info>`
				: this._profileLoading
				? html`<myacc-signin>
                    <myacc-signin-img>
                        <img src=${this._unauthHeaderNode.avatarImg} alt="${this._unauthHeaderNode.avatarImgAltText}"></img>
                    </myacc-signin-img>
                </myacc-signin>`
				: this._profileFallback
				? this.authheaderFallbackContent()
				: html`<myacc-signin>
					<myacc-signin-img>
						<img src=${this._unauthHeaderNode.avatarImg} alt="${this._unauthHeaderNode.avatarImgAltText}"></img>
					</myacc-signin-img>
				</myacc-signin>`}
			${this._rewards && this._unauthNavData.disableRewardsInFlyout !== 'true' ? this.showRewards() : ''}
		</myacc-header>`
	}

	private authheaderFallbackContent() {
		const _cachedName = this._getCachedName()
		return html`<myacc-signin>
			<myacc-signin-img>
				<img src=${this._unauthHeaderNode.avatarImg} alt="${this._unauthHeaderNode.avatarImgAltText}"></img>
			</myacc-signin-img>
			<myacc-signin-message-fallback class="myacc-nav-email">${_cachedName}</myacc-signin-message-fallback>
		</myacc-signin>`
	}

	private _hasValidProfile(): boolean {
		return !!(
			!this._profileFallback &&
			this._profile?.status?.toUpperCase() === 'SUCCESS' &&
			this._profile.first_name !== undefined &&
			this._profile.last_name !== undefined
		)
	}

	private showRewards() {
		return html`<myacc-rewards>
			${this._rewardsState === RewardsState.active
				? this.rewardsContent()
				: this._rewardsState === RewardsState.inactive
				? this.inactiveRewardsContent()
				: this._rewardsState === RewardsState.closed
				? this.closedRewardsContent()
				: this._rewardsState === RewardsState.other
				? this.otherRewardsContent()
				: ''}</myacc-rewards
		>`
	}

	private inactiveRewardsContent() {
		if (this.authFlyoutData.inactive?.inactiveLabel) {
			const inactiveLink =
				this.authFlyoutData.inactive.inactiveLinkType === 'internal'
					? this.authFlyoutData.inactive.inactiveInternalLink
					: this.authFlyoutData.inactive.inactiveExternalLink
			return html`<myacc-enroll>
				<myacc-enroll-text>
					<a
						target="${this.authFlyoutData.inactive.inactiveLinkTarget}"
						href="${inactiveLink}"
						class="stat-button-link"
					>
						${returnRTEHTML(this.authFlyoutData.inactive.inactiveMsg)}
					</a>
				</myacc-enroll-text>
			</myacc-enroll>`
		}
	}

	private closedRewardsContent() {
		if (this.authFlyoutData.closed?.closedLabel) {
			const closedLink =
				this.authFlyoutData.closed.closedLinkType === 'internal'
					? this.authFlyoutData.closed.closedInternalLink
					: this.authFlyoutData.closed.closedExternalLink
			return html`<myacc-enroll>
				<myacc-enroll-text>
					<a
						target="${this.authFlyoutData.closed.closedLinkTarget}"
						href="${closedLink}"
						class="stat-button-link"
					>
						${returnRTEHTML(this.authFlyoutData.closed.closedMsg)}
					</a>
				</myacc-enroll-text>
			</myacc-enroll>`
		}
	}

	private otherRewardsContent() {
		if (this.authFlyoutData.other?.otherLabel) {
			const otherLink =
				this.authFlyoutData.other.otherLinkType === 'internal'
					? this.authFlyoutData.other.otherInternalLink
					: this.authFlyoutData.other.otherExternalLink
			return html`<myacc-enroll>
				<myacc-enroll-text>
					<a
						target="${this.authFlyoutData.other.otherLinkTarget}"
						href="${otherLink}"
						class="stat-button-link"
					>
						${returnRTEHTML(this.authFlyoutData.other.otherMsg)}
					</a>
				</myacc-enroll-text>
			</myacc-enroll>`
		}
	}

	private rewardsContent() {
		const showMinifiedRewardsTxt = this.authNavData.disableRewardsInNav
		const rewardsLink =
			this.authFlyoutData.rewards.rewardsLinkType === 'internal'
				? this.authFlyoutData.rewards.rewardsInternallLink
				: this.authFlyoutData.rewards.rewardsExternalLink

		const rewardsPts = html`<myacc-rewards-points>
			${this._rewards.points ? this._rewards.points : 0} PTS
		</myacc-rewards-points>`
		const tierInfoTxt = html`<myacc-rewards-point-uppertext>
			${this._rewards.tier?.toLowerCase().includes('card')
				? html`<myacc-rewards-point>
						${returnRTEHTML(this.authFlyoutData.rewards.rewardsCardLabel)}
				  </myacc-rewards-point>`
				: html`<myacc-rewards-point>
						${returnRTEHTML(this.authFlyoutData.rewards.rewardsLabel)}
				  </myacc-rewards-point>`}
		</myacc-rewards-point-uppertext>`

		if (showMinifiedRewardsTxt === 'true') {
			return html`<myacc-min-nav-rewards>
				<a
					target="${this.authFlyoutData.rewards.rewardsLinkTarget}"
					href="${rewardsLink}"
					class="stat-button-link"
					data-dtm="my account"
				>
					${tierInfoTxt}<span class="upper-text-seperator">|</span>${rewardsPts}
				</a>
			</myacc-min-nav-rewards>`
		}

		return html`<myacc-nav-rewards>
			<a
				target="${this.authFlyoutData.rewards.rewardsLinkTarget}"
				href="${rewardsLink}"
				class="stat-button-link"
				data-dtm="my account"
			>
				${tierInfoTxt}<span class="upper-text-seperator">|</span>${rewardsPts}
			</a>
		</myacc-nav-rewards>`
	}

	private garageContent(vehicleList: VehicleKeys[]) {
		return html`<myacc-garage>
			${vehicleList.slice(0, 3).map(
				link =>
					html`<myacc-garage-vehicle>
                    <a href="${link['link']}" target="_self" class="stat-image-link" data-dtm="my account:my garage">
                        <myacc-garage-thumbnail>
                                <img src="${link['imgUrl']}" alt="${link['title']}" onerror="this.onerror = null; this.dataset.srcError=this.src;this.src='${this._backupVehicleImagePath}'"></img>
                          </myacc-garage-thumbnail>
                          <myacc-garage-vehiclename>
                                ${link['title']}
                          </myacc-garage-vehiclename>
                        </myacc-vehicle-thumbnail>
                        </a>
                    <myacc-garage-vehicle>`
			)}
			${html`<myacc-viewall-vehicle>
				<myacc-viewall-button>
					<a
						href="${this.authFlyoutData.vehicles.viewAllGarageLink}"
						class="stat-button-link"
						data-dtm="my account:my garage"
						>${this.authFlyoutData.vehicles.viewAllButtonLabel}</a
					>
				</myacc-viewall-button>
			</myacc-viewall-vehicle>`}
		</myacc-garage>`
	}

	private authfooterContent() {
		return html`<myacc-footer>
            <myacc-placeholder>
                <myacc-signout>
                    <button @click="${this.signout}" class="stat-button-link" data-dtm="my account">${this.authFlyoutData.signout.signoutButtonLabel}</button>
                </myacc-placeholder>
            </myacc-placeholder>
        </myacc-footer>`
	}

	private addVehiclesContent() {
		return html`<myacc-add-vehicle>
			<a
				href="${this.authFlyoutData.vehicles.addVehicleLink}"
				target="_self"
				class="stat-image-link"
				data-dtm="my account:my garage"
			>
				<myacc-add-vehicle-img>
					<img
						src="${this.authFlyoutData.vehicles.addVehicleImg}"
						alt="${this.authFlyoutData.vehicles.addVehicleImgAltText}"
					/>
				</myacc-add-vehicle-img>
				<myacc-add-vehicle-label>
					<myacc-add-vehicle-subtext>
						${this.authFlyoutData.vehicles.addVehicleLabel}
					</myacc-add-vehicle-subtext>
				</myacc-add-vehicle-label>
			</a>
		</myacc-add-vehicle>`
	}

	private authlinkContent(position?: string) {
		let linkTemplate = ''
		for (const link in this.authLinkData) {
			const isEnabled = this.authLinkData[link].linkListPosition === position ? true : false
			const linkDtm =
				this?.authLinkData[link]?.dtm1MessageInbox && this?.authLinkData[link]?.dtm1MessageInbox !== ''
					? this?.authLinkData[link]?.dtm1MessageInbox
					: 'my account:account links'
			const isEnabledInAuth =
				this.authLinkData[link].authState === 'true' && this.flyoutState === AuthLiterals.AUTH && isEnabled
					? true
					: false
			const isEnabledInUnauth =
				this.authLinkData[link].unauthState === 'true' && this.flyoutState === AuthLiterals.UNAUTH
					? true
					: false
			if (isEnabledInAuth || isEnabledInUnauth) {
				linkTemplate =
					linkTemplate +
					`<myacc-main-link href=${this.authLinkData[link].link}>
                    <a href=${this.authLinkData[link].link} class="stat-text-link" data-dtm="${linkDtm}">${
						this.authLinkData[link].linkLabel
					}</a>
                    ${
						this.authLinkData[link].subLinkLabel
							? `<myacc-sublink>
                            <a href=${this.authLinkData[link].subLink} class="stat-text-link" data-dtm="${linkDtm}">${this.authLinkData[link].subLinkLabel}</a>
                        </myacc-sublink>`
							: ``
					}
					${
						this.flyoutState === AuthLiterals.AUTH && this._notifications
							? this.renderMessageIndicator(this.authLinkData[link])
							: ''
					}
                </myacc-main-link>`
			}
		}
		if (this.flyoutState === AuthLiterals.AUTH && position === 'above' && this._vehiclesFallback) {
			const fallbackTemplate = this.fallbackData
				? `<myacc-main-link>
                        <a href=${this.fallbackData['fallbackLink']} class="stat-text-link" data-dtm="my account:account links">${this.fallbackData['fallbackLinkLabel']}</a>
					</myacc-main-link>`
				: ''
			linkTemplate = linkTemplate + fallbackTemplate
		}
		return linkTemplate.trim() ? html`<myacc-link> ${returnRTEHTML(linkTemplate)}</myacc-link>` : ''
	}

	renderMessageIndicator(link: AuthLinkKeys) {
		return link['msgInboxLink'] === 'true' && this._notifications['notificationOn']
			? `<span class="myacc-inbox-count">(${this._notifications['unreadCount']})</span><myacc-inbox-notification aria-label="Notification"></myacc-inbox-notification>`
			: ''
	}

	render() {
		if (this.flyoutOpen) {
			return html`${this._setButtonElements()}
			${this.flyoutState === AuthLiterals.AUTH
				? html`${this.authheaderContent()}
						<myacc-auth-flyout-body>
							${this.authlinkContent('above')}
							${this.authNavData.disableAuthVehicles === 'true'
								? ''
								: !this._vehiclesFallback
								? html`${this._vehicles?.length > 0
										? this.garageContent(this._vehicles)
										: this.addVehiclesContent()}`
								: ''}
							${this.authlinkContent('below')} ${this.authfooterContent()}
						</myacc-auth-flyout-body>
						${this.querySelector('myacc-auth-flyout-body') ? this.setFlyoutHeight() : ''}`
				: html`${this.unauthheaderContent()}
						<myacc-auth-flyout-body>
							${this.authlinkContent()} ${this.unauthfooterContent()}
						</myacc-auth-flyout-body>`}`
		}
	}

	createRenderRoot() {
		return this
	}
}
